import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Globe } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import type { WorldBuilderInput, WorldBuilderResult, WorldBuilderDetailedLockedSections } from '@creanizer/types'
import { WorldBuilderForm } from './WorldBuilderForm'
import { WorldBuilderDisplay } from './WorldBuilderDisplay'
import { SavedWorldsList } from './SavedWorldsList'
import { WorldConsistencyCheck } from './WorldConsistencyCheck'

export function WorldBuilder() {
  const { isApiKeySet } = useSettingsStore()
  const { savedWorlds, addWorld } = useWorldBuilderStore()
  const generator = useGenerator('worldbuilder')
  const streamGenerator = useStreamGenerator('worldbuilder')
  
  const [input, setInput] = useState<WorldBuilderInput>({
    mode: 'preset',
    presetType: 'modern-world',
    // プリセットモードではパラメータはプリセットから自動設定
    detailLevel: 5,
    includeTimeline: true,
    includeCultures: true,
    includeGeography: true,
    includeThreatDetails: true,  // 脅威詳細を含める
    creativityLevel: 7,
  })
  
  const [result, setResult] = useState<WorldBuilderResult | null>(null)
  const [error, setError] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState('generate')
  const resultRef = useRef<HTMLDivElement>(null)
  
  // 部分再生成用の状態
  const [partialRegenerateMode, setPartialRegenerateMode] = useState(false)
  const [existingWorldData, setExistingWorldData] = useState<WorldBuilderResult | null>(null)
  const [lockedSections, setLockedSections] = useState<WorldBuilderDetailedLockedSections>({})
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResult(null)
    
    // 部分再生成モードの場合の入力準備
    const finalInput = partialRegenerateMode && existingWorldData
      ? {
          ...input,
          mode: 'partial_regenerate' as const,
          existingResult: existingWorldData,
          lockedSections
        }
      : input
    
    // デバッグ: 参照モードの場合、送信データを確認
    if (input.mode === 'reference') {
      console.log('Reference mode input:', {
        mode: input.mode,
        references: input.references,
        referenceContext: input.referenceContext,
        contextLength: input.referenceContext?.length || 0
      })
    }
    
    try {
      if (useStreaming) {
        await streamGenerator.generateStream(finalInput, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            // Ensure result has required properties
            if (resultData && resultData.name) {
              setResult(resultData as WorldBuilderResult)
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(finalInput)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(finalInput)
        const resultData = response.result || response
        setResult(resultData as WorldBuilderResult)
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }
  
  const handleSave = () => {
    if (!result) return
    
    try {
      addWorld({
        title: result.name,
        input,
        result,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }
  
  const handleLoad = (world: any) => {
    setInput(world.input)
    setResult(world.result)
    setExistingWorldData(world.result)
    setPartialRegenerateMode(true)
    setLockedSections({}) // 全項目をアンロック状態で開始
    setActiveTab('generate')
  }
  
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="h-5 w-5" />
          <h2 className="text-xl font-semibold">世界観ビルダー</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          物語の舞台となる詳細な世界観を生成
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({savedWorlds.length}/500)
          </TabsTrigger>
        </TabsList>
            
        <TabsContent value="generate" className="space-y-4">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            <WorldBuilderForm
                input={input}
                onChange={setInput}
                onGenerate={handleGenerate}
                isGenerating={generator.isPending || streamGenerator.isStreaming}
                useStreaming={useStreaming}
                onStreamingChange={setUseStreaming}
                partialRegenerateMode={partialRegenerateMode}
                existingResult={existingWorldData}
                lockedSections={lockedSections}
                onLockedSectionsChange={setLockedSections}
                onPartialRegenerateModeChange={setPartialRegenerateMode}
            />
          </div>
          
          {streamGenerator.isStreaming && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>生成中...</span>
                    <span>{Math.round(streamGenerator.progress)}%</span>
                  </div>
                  <Progress value={streamGenerator.progress} />
                  {streamGenerator.partialContent && (
                    <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {streamGenerator.partialContent.slice(-500)}
                        {streamGenerator.partialContent.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
          )}
          
          {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SavedWorldsList
            worlds={savedWorlds}
            onLoad={handleLoad}
          />
        </TabsContent>
      </Tabs>
      
      {result && result.name && (
        <div ref={resultRef} className="space-y-6">
          <WorldBuilderDisplay
            result={result}
            onSave={handleSave}
            isSaved={!!savedWorlds.find(w => w.result?.id === result.id)}
            onEdit={(updatedResult) => {
              setResult({ ...result, ...updatedResult })
            }}
          />
          
          {result.consistency && (
            <WorldConsistencyCheck
              consistency={result.consistency}
            />
          )}
        </div>
      )}
    </div>
  )
}